import {
  EntityItemType as EntityType,
  ProductContentUtilizationRecord,
  VideoMetadata
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/ContentServerProxy/types';
import { ContentUtilizationType } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/ContentBarPercent/utils';
import { getDurationString, getRatioString, getResolutionString } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/adapters/ProductContentAccuracyAdapter';
import { ChartDataAdapter, UtilizationData } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/adapters/types';

/**
 * Creates a placeholder section for a section if the section is missing from the API response.
 *
 * @param sectionTitle - The title of the section to create a placeholder for.
 * @returns A UtilizationData object with default values based on the section type.
 */
const getPlaceholderSection = (sectionTitle: string): UtilizationData => {
  const sectionType = sectionTitle.split(' ')[0];

  switch (sectionType) {
    case ContentUtilizationType.Images:
      return { title: sectionTitle, content: [], type: 'image', currentUtilization: 0, maxUtilization: 6, score: 0 };
    case ContentUtilizationType.Video:
      return { title: sectionTitle, content: [], type: 'video', currentUtilization: 0, maxUtilization: 4, score: 0 };
    case ContentUtilizationType.EnhancedContent:
      return { title: sectionTitle, content: null, type: 'aPlusContent', currentUtilization: false };
    case ContentUtilizationType.Title:
    case ContentUtilizationType.Bullet:
    case ContentUtilizationType.Description:
    default:
      return {
        title: sectionTitle,
        content: `Missing required ${sectionType.toLowerCase()}.`,
        type: 'character',
        currentUtilization: 0,
        maxUtilization: 0,
        score: 0
      };
  }
};

const mapDataToSections = (data: UtilizationData[]): UtilizationData[] => {
  const groupedSections = data.reduce((acc, item) => {
    const baseType = item.title.split(' ')[0];
    if (!acc[baseType]) {
      acc[baseType] = [];
    }
    acc[baseType].push(item);
    return acc;
  }, {} as Record<string, UtilizationData[]>);

  if (groupedSections.Bullet) {
    groupedSections.Bullet.sort((a, b) => {
      const aNum = parseInt(a.title.split(' ')[1], 10);
      const bNum = parseInt(b.title.split(' ')[1], 10);
      return aNum - bNum;
    });
  }

  const orderedSections: UtilizationData[] = [];

  orderedSections.push(data.find((item) => item.title === ContentUtilizationType.Title) ?? getPlaceholderSection(ContentUtilizationType.Title));

  // Add all available bullets
  const bulletSections = groupedSections.Bullet || [];
  if (bulletSections.length === 0) {
    for (let i = 1; i <= 5; i++) {
      orderedSections.push(getPlaceholderSection(`${ContentUtilizationType.Bullet} ${i}`));
    }
  } else {
    orderedSections.push(...bulletSections);
  }

  orderedSections.push(data.find((item) => item.title === ContentUtilizationType.Description) ?? getPlaceholderSection(ContentUtilizationType.Description));

  [ContentUtilizationType.Images, ContentUtilizationType.Video, ContentUtilizationType.EnhancedContent].forEach((sectionTitle) => {
    orderedSections.push(data.find((item) => item.title === sectionTitle) ?? getPlaceholderSection(sectionTitle));
  });

  return orderedSections;
};

class ProductContentUtilizationRecordAdapter {
  record: ProductContentUtilizationRecord;

  constructor(record: ProductContentUtilizationRecord) {
    this.record = record;
  }

  private createBaseUtilization(title: string) {
    return {
      title,
      maxUtilization: this.record.metrics?.expectedCount || 0,
      currentUtilization: this.record.metrics?.actualCount || 0,
      score: this.record.metrics?.score || 0
    };
  }

  private transformTextContent(title: string) {
    return {
      ...this.createBaseUtilization(title),
      type: 'character' as const,
      content: this.record.entityValue || `This product has no ${title.toLowerCase()}.`
    };
  }

  private transformBulletPoints() {
    // TODO(tyan): define default max value somewhere, also determine behavior if there are more bullets than expected

    const actualBullets = this.record.children?.length || 0;

    return [...Array(actualBullets)].map((_, index) => {
      const bullet = index < actualBullets ? this.record.children?.[index] : undefined;
      return {
        title: `${ContentUtilizationType.Bullet} ${index + 1}`,
        content: bullet?.entityValue || 'Missing required bullet.',
        maxUtilization: bullet?.metrics?.expectedCount || 0,
        currentUtilization: bullet?.metrics?.actualCount || 0,
        score: bullet?.metrics?.score || 0,
        type: 'character' as const
      };
    });
  }

  private transformImages() {
    const expectedImages = this.record.metrics?.expectedCount || 6;
    const actualImages = this.record.children?.length || 0;
    const images = [...Array(Math.max(expectedImages, actualImages))].map((_, index) => {
      const image = index < actualImages ? this.record.children?.[index] : undefined;
      return image
        ? {
          url: image.entityValue || '',
          altText: `${ContentUtilizationType.Image} ${index + 1}`,
          resolution: getResolutionString(image.entityMetadata),
          ratio: getRatioString(image.entityMetadata)
        }
        : null;
    });

    return {
      ...this.createBaseUtilization(ContentUtilizationType.Images),
      type: 'image' as const,
      content: images
    };
  }

  private transformVideo() {
    const expectedVideos = this.record?.metrics?.expectedCount || 1;
    const actualVideos = this.record?.children?.length || 0;

    const videos = [...Array(Math.max(expectedVideos, actualVideos))].map((_, index) => {
      const video = index < actualVideos ? this.record.children?.[index] : undefined;
      return video
        ? {
          url: video?.entityValue || '',
          resolution: getResolutionString(video.entityMetadata),
          length: getDurationString(video.entityMetadata as VideoMetadata)
        }
        : null;
    });

    return {
      ...this.createBaseUtilization(ContentUtilizationType.Video),
      type: 'video' as const,
      content: videos
    };
  }

  toUtilizationData() {
    switch (this.record.entityType) {
      case EntityType.Title:
        return this.transformTextContent(ContentUtilizationType.Title);
      case EntityType.BulletPoint:
        return this.transformBulletPoints();
      case EntityType.Description:
        return this.transformTextContent(ContentUtilizationType.Description);
      case EntityType.Image:
        return this.transformImages();
      case EntityType.Video:
        return this.transformVideo();
      case EntityType.APlusContent:
        return {
          title: ContentUtilizationType.EnhancedContent,
          content: null,
          type: 'aPlusContent' as const,
          currentUtilization: this.record.metrics?.score === 1
        };
      default:
        return null;
    }
  }
}

export class ProductContentUtilizationAdapter implements ChartDataAdapter<UtilizationData[]> {
  data: ProductContentUtilizationRecord[];

  constructor(data: ProductContentUtilizationRecord[]) {
    this.data = data;
  }

  transform(): UtilizationData[] {
    const transformedData: UtilizationData[] = this.data

      .map((record) => {
        return new ProductContentUtilizationRecordAdapter(record).toUtilizationData();
      })
      .filter((item) => item !== null)
      .flat();

    return mapDataToSections(transformedData);
  }
}
